/**
 * @fileOverview The customers walkthrough: the list, and one customer's page with its nine registers.
 * Pure data. See docs/view-help.md and docs/view-help/customers.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading customers — the list and a customer's page need it (`router/routes/customer.js`). */
const VIEW = [PERMISSIONS.VIEW_CUSTOMER_GENERAL]

/** Entering a new one — the create button. */
const EDIT = [PERMISSIONS.EDIT_CUSTOMER_GENERAL]

/** Deleting one — the only item the actions menu holds, so the menu only exists with it. */
const DELETE = [PERMISSIONS.DELETE_CUSTOMER_GENERAL]

/** Assigning a machine to a customer — what the assign dialog does. */
const EDIT_MACHINE = [PERMISSIONS.EDIT_CUSTOMER_MACHINE]

/** The sample customer every step of its page is read on — the world's Beispiel GmbH. */
const CUSTOMER = { customerId: "c-beispiel" }

const onOverview = stepsOn("CustomerOverview", { permissions: VIEW })
const onDetail = stepsOn("CustomerDetail", { permissions: VIEW, at: { params: CUSTOMER } })

// The related views: where a customer takes effect outside its own page, and what it is read against.
// A project is where one is picked, and the workspace's Standardwährung is the currency every amount
// in a customer's record is written in. The pointer walks there (`services/viewHelp/ways.ts`); `at`
// says where to load the frame if that walk ever fails.
const onProject = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_GENERAL, PERMISSIONS.VIEW_FOREIGN_PROJECT_GENERAL],
  at: { params: { projectId: "p-nord" }, hash: "general" },
})
// The settings page is open to everybody signed in, so the step carries no gate of its own.
const onSettings = stepsOn("SettingsOverview", { at: { hash: "general" } })

/**
 * Customers: the record every project hangs off, and the place a firm's own prices are negotiated.
 *
 * The list first; then Beispiel GmbH's page, whose registers the pointer walks through one after
 * another — the contacts a project may then use, the three price lists, the negotiated hourly rates —
 * with the machine assign dialog opened, a row ticked and taken over, and one price panel opened on
 * its editor.
 *
 * Only six of the nine registers can be pointed at: the frame is 1280px wide, where the strip shows
 * six tabs and slides the rest behind its arrow, and a slid-away tab has a healthy box that is nowhere
 * on screen (`isAnchorVisible` refuses it). Dateien, Einstellungen and Aktivität are therefore named
 * in the text of the step on the strip rather than given steps of their own.
 *
 * The create page is not walked: the create button on the list is pointed at, and that is all. So the
 * trigger is not offered there either — pressed on the form, it would have opened on the list.
 */
export const CUSTOMERS_TOUR: ViewHelpTour = {
  key: "customers",
  icon: ENTITY_ICONS.customer,
  routes: ["CustomerOverview", "CustomerDetail"],
  // Where a customer shows up outside its own page: the project that picks it, and the workspace's
  // Standardwährung its amounts are written in. Played last, after a rule on the timeline.
  related: ["ProjectDetail", "SettingsOverview"],
  scenes: [
    // The actions menu, opened to show what is in it. Its one item deletes the customer, so nothing in
    // it is pressed; Escape closes the menu again.
    { key: "actions", enter: [{ do: "click", target: "customer-actions" }], exit: [{ do: "escape" }] },
    // The walk through the registers: every step presses its own tab, so each is reachable on its own
    // and going from one to the next is a single click. Nothing to put back — the tab that is open is
    // where the next scene starts from.
    { key: "tabs", enter: [], exit: [] },
    // The machine assign dialog, opened from the register the step before has just pressed, a row
    // ticked and taken over with Hinzufügen. `openTab` rather than `click`: played on from that step the
    // tab is already open and is not pressed a second time, and from anywhere else on the timeline it
    // is what makes the dialog reachable. Hinzufügen closes the dialog itself and writes nothing — the
    // new row only joins the unsaved form — so the exit is only for a reader who leaves mid-dialog.
    {
      key: "machine-assign",
      enter: [
        { do: "openTab", target: "customer-tab-machines" },
        { do: "click", target: "customer-machine-assign" },
      ],
      exit: [{ do: "escape" }],
    },
    // The next two registers. A scene of their own because the dialog sits between them and the machine
    // register, and a scene's steps have to be consecutive.
    { key: "price-tabs", enter: [], exit: [] },
    // One price panel opened on its editor, and closed again: open, it pushes the rest of the list down.
    {
      key: "pricing",
      enter: [
        { do: "openTab", target: "customer-tab-additionalEfforts" },
        { do: "expand", target: "customer-effort-first" },
      ],
      exit: [{ do: "collapse", target: "customer-effort-first" }],
    },
    // The last register of the walk, for the same reason as `price-tabs`.
    { key: "positions-tab", enter: [], exit: [] },
  ],
  steps: [
    // ── Übersicht ────────────────────────────────────────────────────────────
    onOverview("list", "customer-table", ENTITY_ICONS.customer, { placement: "bottom" }),
    onOverview("row", "row-c-beispiel", "mdi-table-row", { placement: "bottom" }),
    // Pointed at, not pressed: the create form is not part of this walkthrough.
    onOverview("create", "customer-create", "mdi-plus", { placement: "bottom", permissions: EDIT }),

    // ── Beispiel GmbH ────────────────────────────────────────────────────────
    onDetail("head", "customer-head", ENTITY_ICONS.customer, { placement: "bottom" }),
    // The text sits beside the button, not below it: below is where the menu opens, and a bubble there
    // covered the very item the step is about. No zoom for the same reason — zoomed onto a 134px
    // button, the menu that drops out of it was the only thing left in the picture.
    onDetail("actions", "customer-actions", "mdi-dots-horizontal", {
      placement: "left",
      scene: "actions",
      zoom: false,
      permissions: DELETE,
    }),
    onDetail("tabs", "customer-tabs", "mdi-tab", { placement: "bottom" }),
    // Every register step presses its own tab, with the whole page in shot: zoomed onto the tab, the
    // panel that slides in underneath would be out of the picture.
    onDetail("general", "customer-general", "mdi-form-textbox", {
      placement: "top",
      scene: "tabs",
      zoom: false,
      actions: [{ do: "openTab", target: "customer-tab-general" }],
    }),
    onDetail("contacts", "customer-contacts", "mdi-account-multiple-outline", {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      permissions: [PERMISSIONS.VIEW_CUSTOMER_CONTACT],
      actions: [{ do: "openTab", target: "customer-tab-contacts" }],
    }),
    onDetail("machines", "customer-machines", ENTITY_ICONS.machine, {
      placement: "bottom",
      scene: "tabs",
      zoom: false,
      permissions: [PERMISSIONS.VIEW_CUSTOMER_MACHINE],
      actions: [{ do: "openTab", target: "customer-tab-machines" }],
    }),
    // The window whole first, then the camera goes in on its list of hits.
    onDetail("assign-open", "customer-machine-assign-dialog", "mdi-plus", {
      placement: "top",
      scene: "machine-assign",
      permissions: EDIT_MACHINE,
    }),
    onDetail("assign-pick", "customer-machine-assign-results", "mdi-checkbox-marked-outline", {
      placement: "bottom",
      scene: "machine-assign",
      permissions: EDIT_MACHINE,
      actions: [{ do: "click", target: "customer-machine-assign-results" }],
    }),
    // Hinzufügen pressed, and the step explained on the register it has just added a row to — with the
    // whole page in shot, since the dialog closing and the row appearing are the point.
    onDetail("assign-add", "customer-machines", "mdi-plus-circle-outline", {
      placement: "bottom",
      scene: "machine-assign",
      zoom: false,
      permissions: EDIT_MACHINE,
      actions: [{ do: "click", target: "customer-machine-assign-add" }],
    }),
    onDetail("materials", "customer-materials", ENTITY_ICONS.material, {
      placement: "bottom",
      scene: "price-tabs",
      zoom: false,
      permissions: [PERMISSIONS.VIEW_CUSTOMER_MATERIAL],
      actions: [{ do: "openTab", target: "customer-tab-materials" }],
    }),
    onDetail("efforts", "customer-efforts", ENTITY_ICONS.additionaleffort, {
      placement: "bottom",
      scene: "price-tabs",
      zoom: false,
      permissions: [PERMISSIONS.VIEW_CUSTOMER_ADDITIONAL_EFFORT],
      actions: [{ do: "openTab", target: "customer-tab-additionalEfforts" }],
    }),
    onDetail("price-modes", "customer-effort-pricing", "mdi-cash-multiple", {
      placement: "bottom",
      scene: "pricing",
      permissions: [PERMISSIONS.VIEW_CUSTOMER_ADDITIONAL_EFFORT_SELLING_PRICE],
    }),
    onDetail("positions", "customer-positions", ENTITY_ICONS.userposition, {
      placement: "bottom",
      scene: "positions-tab",
      zoom: false,
      permissions: [PERMISSIONS.VIEW_CUSTOMER_USERPOSITION],
      actions: [{ do: "openTab", target: "customer-tab-userPositions" }],
    }),
    // Explained, never pressed: it writes every register at once.
    onDetail("save", "customer-save", "mdi-content-save-outline", { placement: "top" }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Not a tour of two more pages: what matters here is only where a customer takes effect outside its
    // own page. So each step names the place and what the customer does to it.
    onProject("project-customer", "project-general-customer", ENTITY_ICONS.project, { placement: "top" }),
    onSettings("currency", "settings-currency", "mdi-currency-eur", { placement: "bottom" }),
  ],
}
